import i18next, { TFunction } from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Object3D } from 'three';

import { Glb } from '../../../../../viewer/elements/glb';
import {
  IfcObject,
  IfcValue,
  isIfcSpatial,
  LayerTreeNode,
  showOnlyLayer,
} from '../../../../../viewer/elements/glb-layers';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { Point2d } from '../../../../../viewer/utilities/math-helpers';
import { useDialog } from '../../../../hooks/use-dialog';
import { useNonce } from '../../../../hooks/use-nonce';
import { useSubscribeCallback } from '../../../../hooks/use-subscribe';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { MenuItem } from '../../../context-menu/components/menu-item';
import { ContextMenu } from '../../../context-menu/context-menu';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { getAssetDialogPosition, getBesideAssetMenuPosition } from './asset-dialog-position';

type Props = {
  asset: Glb;
  dialog: ReturnType<typeof useDialog>;
  /**
   * Where the icon was clicked: the dialog opens beside it, near the top of the screen. Without it,
   * when opened as the asset is selected, the dialog opens collapsed beside the asset menu, and
   * closes when the asset is unselected.
   */
  pos?: Point2d;
};

type IconProps = {
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
};

export const LayerVisibilityIcon = (props: IconProps) => {
  const { t } = useTranslation();

  return (
    <Icon
      disabled={props.disabled}
      fixedWidth={true}
      icon={['fal', 'layer-group']}
      mouseDownStyle={{ opacity: 0.5 }}
      stopPropagationOnClick={true}
      title={t('layerVisibilityDialog.title', { ns: 'skyviewAssetMenu' })}
      onClick={props.onClick}
      onHoverStyle={{ icon: ['fad', 'layer-group'] }}
    />
  );
};

/**
 * Whether the asset has layers that can be shown and hidden: the layers of a PDF or DWG, or the
 * objects of an IFC model, which are loaded while the asset is shown.
 */
export const hasLayers = (asset: Glb) =>
  asset.getStateVisibility() && asset.loadingStatus === 'loaded' && asset.getLayerTree().length > 0;

/**
 * Opens the layers of an asset that has them when it is selected, see Props.pos, or when they have
 * loaded if it is selected before. It is opened once per selection: it can be closed, and opened
 * again from its icon. It is also opened when a layer of the asset is inspected in the scene.
 */
export const useOpenLayerVisibilityOnSelection = (
  asset: Glb | undefined,
  dialog: ReturnType<typeof useDialog>,
) => {
  // Whether the asset is selected, and the dialog has not been opened for that selection yet.
  const pending = React.useRef(false);

  const openIfPending = () => {
    if (!pending.current || !asset || !hasLayers(asset)) {
      return;
    }

    pending.current = false;

    // When opened from the icon, it stays beside the icon.
    if (!dialog.visible) {
      dialog.show();
    }
  };

  const openIfInspected = () => {
    if (asset?.inspectedLayer && !dialog.visible) {
      pending.current = false;
      dialog.show();
    }
  };

  useSubscribeCallback(SubscriptionTopic.NewSelection, (selected) => {
    pending.current = selected.uuid === asset?.uuid;
    openIfPending();
  });
  useSubscribeCallback(SubscriptionTopic.UnSelection, () => {
    pending.current = false;
  });
  useSubscribeCallback(SubscriptionTopic.AssetChanged, (changed) => {
    if (changed.uuid === asset?.uuid) {
      openIfPending();
    }
  });
  useSubscribeCallback(SubscriptionTopic.LayerInspected, (changed) => {
    if (changed.uuid === asset?.uuid) {
      openIfInspected();
    }
  });

  // Also when the asset menu lists the asset after it was inspected, e.g. scrolled to it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(openIfInspected, []);
};

/**
 * Closes the dialog when the layers of its asset can no longer be shown and hidden, e.g. when it
 * is hidden, as their icon is then gone, and when the asset is unselected if the dialog was opened
 * as it was selected.
 */
const useClose = (asset: Glb, close: () => void, openedOnSelection: boolean) => {
  const closeIfNoLayers = () => {
    if (!hasLayers(asset)) {
      close();
    }
  };

  useSubscribeCallback(SubscriptionTopic.SkyviewAssetVisibilityChanged, closeIfNoLayers);
  useSubscribeCallback(SubscriptionTopic.AssetChanged, closeIfNoLayers);
  useSubscribeCallback(SubscriptionTopic.UnSelection, () => {
    if (openedOnSelection) {
      close();
    }
  });
  useSubscribeCallback(SubscriptionTopic.NewSelection, (selected) => {
    if (openedOnSelection && selected.uuid !== asset.uuid) {
      close();
    }
  });
};

/**
 * A layer is unfolded when the dialog opens if there are at most this many layers below it.
 */
const maxUnfoldedLayers = 50;

/**
 * How many layers are listed below a layer at first, and more each time more are asked for, since
 * an IFC model can have thousands of objects in a storey.
 */
export const layerPageSize = 100;

const hasAtMostLayers = (nodes: LayerTreeNode[], max: number) => {
  let count = 0;
  const visit = (nodes: LayerTreeNode[]): boolean =>
    nodes.every((node) => ++count <= max && visit(node.children));
  return visit(nodes);
};

const forEachLayer = (nodes: LayerTreeNode[], callback: (node: LayerTreeNode) => void) => {
  for (const node of nodes) {
    callback(node);
    forEachLayer(node.children, callback);
  }
};

/**
 * Which layers of a tree are unfolded and how many are listed below them, see layerPageSize, kept
 * while the dialog is open.
 */
class TreeView {
  private unfolded = new Set<LayerTreeNode>();
  /** How many of the layers are listed, by the list of layers: the top, or below a layer. */
  private listed = new Map<LayerTreeNode[], number>();
  /** The layer of each node, with the layer above it, see reveal. */
  private layers = new Map<Object3D, { node: LayerTreeNode; parent?: LayerTreeNode }>();

  constructor(private tree: LayerTreeNode[]) {
    const index = (nodes: LayerTreeNode[], parent?: LayerTreeNode) => {
      for (const node of nodes) {
        this.layers.set(node.object, { node, parent });
        index(node.children, node);
      }
    };

    index(tree);
    tree.forEach((node) => this.unfold(node));
  }

  /**
   * Gets the layer of the node, if it is one.
   */
  getLayer(object: Object3D) {
    return this.layers.get(object)?.node;
  }

  isUnfolded(node: LayerTreeNode) {
    return this.unfolded.has(node);
  }

  /**
   * Unfolds the layer, and below it the layers with few layers below them, e.g. the storeys of an
   * IFC model, and those with its spatial structure below them, e.g. a building, but not the
   * storeys with their elements or the nodes of a large glb.
   */
  unfold(node: LayerTreeNode) {
    this.unfolded.add(node);

    for (const child of node.children) {
      if (
        hasAtMostLayers(child.children, maxUnfoldedLayers) ||
        child.children.some((x) => isIfcSpatial(x.object))
      ) {
        this.unfold(child);
      }
    }
  }

  fold(node: LayerTreeNode) {
    this.unfolded.delete(node);
  }

  /**
   * How many of the layers of the list are listed.
   */
  getListed(nodes: LayerTreeNode[]) {
    return Math.min(nodes.length, this.listed.get(nodes) ?? layerPageSize);
  }

  listMore(nodes: LayerTreeNode[]) {
    this.listed.set(nodes, this.getListed(nodes) + layerPageSize);
  }

  /**
   * Unfolds the layers above the node's, and lists it, so that it is shown, e.g. when it is
   * inspected in the scene. Returns whether it is a layer.
   */
  reveal(object: Object3D) {
    let layer = this.layers.get(object);
    if (!layer) {
      return false;
    }

    while (layer) {
      const siblings = layer.parent?.children ?? this.tree;
      const index = siblings.indexOf(layer.node);
      if (index >= this.getListed(siblings)) {
        this.listed.set(siblings, Math.ceil((index + 1) / layerPageSize) * layerPageSize);
      }

      if (!layer.parent) {
        break;
      }

      this.unfolded.add(layer.parent);
      layer = this.layers.get(layer.parent.object);
    }

    return true;
  }
}

type TreeContext = {
  asset: Glb;
  view: TreeView;
  onChange: () => void;
  onContextMenu: (node: LayerTreeNode, e: React.MouseEvent) => void;
  onInspect: (node: LayerTreeNode) => void;
  refresh: () => void;
  /** The row of the inspected layer, to scroll to when it is revealed. */
  inspectedRowRef: React.RefObject<HTMLDivElement>;
};

const TreeContext = React.createContext<TreeContext>(undefined!);

/**
 * Shows and hides the layers of a PDF or DWG, or the objects of an IFC model, in a tree. A layer is
 * hidden with the nodes above it, so the layers below a hidden one are dimmed. Hovering a layer
 * highlights it in the scene, and clicking it inspects it: it stays highlighted, and the IFC data of
 * an object of an IFC model is shown below the tree, as when it is inspected in the scene.
 */
export const LayerVisibilityDialog = (props: Props) => {
  const { t } = useTranslation();
  const [_, refresh] = useNonce();
  const { asset } = props;
  const glbData = asset.glbData;
  const tree = useMemo(() => (glbData ? asset.getLayerTree() : []), [asset, glbData]);
  const view = useMemo(() => new TreeView(tree), [tree]);
  const [contextMenu, setContextMenu] = useState<{ pos: Point2d; node: LayerTreeNode }>();
  // Opened as the asset is selected, it is collapsed, unless a layer is inspected in the scene.
  const [collapsed, setCollapsed] = useState(!props.pos && !asset.inspectedLayer);
  // Opened from the icon again while it is open, e.g. collapsed as the asset was selected: it opens
  // beside the icon, unfolded, as the dialog is made again there (see its key).
  const [openedAt, setOpenedAt] = useState(props.pos);
  if (props.pos !== openedAt) {
    setOpenedAt(props.pos);
    if (props.pos) {
      setCollapsed(false);
    }
  }
  const inspectedRowRef = useRef<HTMLDivElement>(null);
  // Whether to scroll to the inspected layer once it is listed.
  const scrollToInspected = useRef(false);

  // Whether a layer has been inspected, e.g. in the scene, which opens the dialog without selecting
  // the asset: it then stays open as the selection changes, until it is closed.
  const [inspectedOnce, setInspectedOnce] = useState(!!asset.inspectedLayer);

  const close = () => {
    asset.inspectLayer(undefined);
    props.dialog.hide();
  };

  useClose(asset, close, !props.pos && !inspectedOnce);

  const reveal = () => {
    if (asset.inspectedLayer && view.reveal(asset.inspectedLayer)) {
      scrollToInspected.current = true;
    }
  };

  // The layer that was inspected in the scene before the dialog opened, or before the tree changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(reveal, [view]);

  useSubscribeCallback(SubscriptionTopic.LayerInspected, (changed) => {
    if (changed.uuid === asset.uuid) {
      reveal();
      if (asset.inspectedLayer) {
        setCollapsed(false);
        setInspectedOnce(true);
      }
      refresh();
    }
  });
  useSubscribeCallback(SubscriptionTopic.LayerVisibilityChanged, (changed) => {
    if (changed.uuid === asset.uuid) {
      refresh();
    }
  });
  // Also when an object of an IFC model is hovered in the scene.
  useSubscribeCallback(SubscriptionTopic.LayerHovered, (changed) => {
    if (changed.uuid === asset.uuid) {
      refresh();
    }
  });

  useEffect(() => {
    if (scrollToInspected.current && inspectedRowRef.current) {
      scrollToInspected.current = false;
      // Not in tests, whose elements are not laid out.
      inspectedRowRef.current.scrollIntoView?.({ block: 'nearest' });
    }
  });

  // A hovered layer is no longer highlighted when the dialog is closed.
  useEffect(() => () => asset.hoverLayer(undefined), [asset]);

  const onChange = () => {
    asset.saveLayerVisibility();
    refresh();
  };

  const setAllVisible = (visible: boolean) => {
    forEachLayer(tree, (node) => (node.object.visible = visible));
    onChange();
  };

  const onContextMenu = (node: LayerTreeNode, e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ pos: { x: e.clientX, y: e.clientY }, node });
  };

  const context: TreeContext = {
    asset,
    view,
    onChange,
    onContextMenu,
    onInspect: (node) => asset.inspectLayer(node.object),
    refresh,
    inspectedRowRef,
  };

  const inspected = asset.inspectedLayer && view.getLayer(asset.inspectedLayer);
  const ifcObject = inspected && asset.getIfcObject(inspected.object);

  return (
    <Dialog
      // Beside the asset menu, below the other dialogs there, as it opens as the asset is selected.
      autoDock={!props.pos}
      closeIcon={true}
      collapsed={collapsed}
      collapsible={true}
      contentPadding={false}
      disableDimmer={true}
      // The tree and the inspector below it scroll by themselves.
      fillContent={true}
      // Not when opened as the asset is selected, which is not asked for.
      focusOnOpen={!!props.pos}
      // Opened from the icon again, e.g. after it was opened collapsed as the asset was selected:
      // it opens beside the icon, unfolded.
      key={props.pos ? `${props.pos.x},${props.pos.y}` : undefined}
      maxWidth={400}
      movable={true}
      position={props.pos ? getAssetDialogPosition(props.pos) : getBesideAssetMenuPosition()}
      onClose={close}
      onCollapsedChanged={setCollapsed}
    >
      {{
        // The file of the asset, as in the asset menu.
        header: asset.fileInfo?.name ?? asset.name,
        content: (
          <>
            <Panes $inspecting={!!ifcObject}>
              <Tree>
                <TreeContext.Provider value={context}>
                  <LayerList level={0} nodes={tree} shown={true} />
                </TreeContext.Provider>
              </Tree>
              {ifcObject && (
                <IfcInspector
                  name={getName(t, inspected)}
                  object={ifcObject}
                  onClose={() => asset.inspectLayer(undefined)}
                />
              )}
            </Panes>
            {contextMenu && (
              <ContextMenu
                left={contextMenu.pos.x}
                top={contextMenu.pos.y}
                onClose={() => setContextMenu(undefined)}
              >
                <MenuItem
                  label={t('layerVisibilityDialog.showOnly', { ns: 'skyviewAssetMenu' })}
                  onClick={() => {
                    showOnlyLayer(tree, contextMenu.node);
                    onChange();
                  }}
                />
              </ContextMenu>
            )}
          </>
        ),
        footer: {
          left: (
            <>
              <Button type="button" variant="text" onClick={() => setAllVisible(true)}>
                {t('layerVisibilityDialog.showAll', { ns: 'skyviewAssetMenu' })}
              </Button>
              <Button type="button" variant="text" onClick={() => setAllVisible(false)}>
                {t('layerVisibilityDialog.hideAll', { ns: 'skyviewAssetMenu' })}
              </Button>
            </>
          ),
          right: (
            <Button color="primary" type="button" variant="contained" onClick={close}>
              {t('close', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};

/**
 * Names an unnamed layer: by what it is in an IFC model, e.g. a storey, as there can be several.
 */
const getUnnamedName = (t: TFunction, ifcClass?: string) => {
  switch (ifcClass) {
    case 'IfcProject':
      return t('layerVisibilityDialog.ifc.project', { ns: 'skyviewAssetMenu' });
    case 'IfcSite':
      return t('layerVisibilityDialog.ifc.site', { ns: 'skyviewAssetMenu' });
    case 'IfcBuilding':
      return t('layerVisibilityDialog.ifc.building', { ns: 'skyviewAssetMenu' });
    case 'IfcBuildingStorey':
      return t('layerVisibilityDialog.ifc.storey', { ns: 'skyviewAssetMenu' });
    default:
      // The other objects of an IFC model, e.g. an IfcWall.
      return ifcClass ?? t('layerVisibilityDialog.unnamed', { ns: 'skyviewAssetMenu' });
  }
};

const getName = (t: TFunction, node: LayerTreeNode) =>
  node.page !== undefined
    ? t('layerVisibilityDialog.page', { ns: 'skyviewAssetMenu', page: node.page })
    : node.name || getUnnamedName(t, node.ifcClass);

const LayerList = (props: {
  nodes: LayerTreeNode[];
  level: number;
  /** Whether the layers above are shown. */
  shown: boolean;
}) => {
  const { t } = useTranslation();
  const { view, refresh } = React.useContext(TreeContext);
  const listed = view.getListed(props.nodes);
  const more = props.nodes.length - listed;

  return (
    <List>
      {props.nodes.slice(0, listed).map((node) => (
        <LayerItem key={node.object.uuid} level={props.level} node={node} shown={props.shown} />
      ))}
      {more > 0 && (
        <li>
          <Row $dimmed={false} $level={props.level}>
            <Button
              type="button"
              variant="text"
              onClick={() => {
                view.listMore(props.nodes);
                refresh();
              }}
            >
              {t('layerVisibilityDialog.listMore', {
                ns: 'skyviewAssetMenu',
                count: Math.min(more, layerPageSize),
                total: props.nodes.length,
              })}
            </Button>
          </Row>
        </li>
      )}
    </List>
  );
};

const LayerItem = (props: { node: LayerTreeNode; level: number; shown: boolean }) => {
  const { t } = useTranslation();
  const { asset, view, onChange, onContextMenu, onInspect, refresh, inspectedRowRef } =
    React.useContext(TreeContext);
  const { node } = props;
  const expanded = view.isUnfolded(node);
  const hasChildren = node.children.length > 0;
  const inspected = asset.inspectedLayer === node.object;
  const name = getName(t, node);

  return (
    <li>
      <Row
        $dimmed={!props.shown}
        $level={props.level}
        $selectable={true}
        data-hovered={asset.hoveredLayer === node.object}
        data-inspected={inspected}
        ref={inspected ? inspectedRowRef : undefined}
        onClick={() => onInspect(node)}
        onContextMenu={(e) => onContextMenu(node, e)}
        onMouseEnter={() => asset.hoverLayer(node.object)}
        onMouseLeave={() => asset.hoverLayer(undefined)}
      >
        <Expander $hidden={!hasChildren}>
          <Icon
            fixedWidth={true}
            icon={['fas', expanded ? 'caret-down' : 'caret-right']}
            stopPropagationOnClick={true}
            onClick={() => {
              if (expanded) {
                view.fold(node);
              } else {
                view.unfold(node);
              }
              refresh();
            }}
          />
        </Expander>
        <CheckBox
          aria-label={name}
          checked={node.object.visible}
          title={name}
          onChange={() => {
            node.object.visible = !node.object.visible;
            onChange();
          }}
        />
        <Name title={name}>{name}</Name>
      </Row>
      {hasChildren && expanded && (
        <LayerList
          level={props.level + 1}
          nodes={node.children}
          shown={props.shown && node.object.visible}
        />
      )}
    </li>
  );
};

/**
 * Formats a value of the IFC data, see IfcValue. Objects are listed as rows of their own.
 */
const formatIfcValue = (t: TFunction, value: IfcValue): string => {
  if (value === null) {
    return t('layerVisibilityDialog.ifc.unknown', { ns: 'skyviewAssetMenu' });
  }

  if (typeof value === 'boolean') {
    return value ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' });
  }

  if (typeof value === 'number') {
    return value.toLocaleString(i18next.language, { maximumFractionDigits: 4, useGrouping: false });
  }

  if (Array.isArray(value)) {
    return value.map((x) => formatIfcValue(t, x)).join(', ');
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([name, x]) => `${name}: ${formatIfcValue(t, x)}`)
      .join(', ');
  }

  return value;
};

const isIfcObjectValue = (value: IfcValue): value is { [name: string]: IfcValue } =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const IfcRows = (props: { values: Record<string, IfcValue>; level?: number }) => {
  const { t } = useTranslation();
  const level = props.level ?? 0;

  return (
    <>
      {Object.entries(props.values).map(([name, value]) =>
        isIfcObjectValue(value) ? (
          // A complex property, or the bounds of a bounded value.
          <React.Fragment key={name}>
            <tr>
              <Key $level={level} colSpan={2}>
                {name}
              </Key>
            </tr>
            <IfcRows level={level + 1} values={value} />
          </React.Fragment>
        ) : (
          <tr key={name}>
            <Key $level={level}>{name}</Key>
            <Value>{formatIfcValue(t, value)}</Value>
          </tr>
        ),
      )}
    </>
  );
};

const IfcSection = (props: { title: string; values: Record<string, IfcValue> }) => (
  <Section open={true}>
    <summary>{props.title}</summary>
    <table>
      <tbody>
        <IfcRows values={props.values} />
      </tbody>
    </table>
  </Section>
);

/**
 * The IFC data of an inspected object of an IFC model: its attributes, type, property and quantity
 * sets, classifications and materials. It stays below the tree while the tree is scrolled.
 */
const IfcInspector = (props: { name: string; object: IfcObject; onClose: () => void }) => {
  const { t } = useTranslation();
  const { object } = props;

  return (
    <Inspector aria-label={props.name} role="region">
      <InspectorHeader>
        <Name>
          <strong>{props.name}</strong> {object.class}
        </Name>
        <Icon
          fixedWidth={true}
          icon="xmark"
          title={t('close', { ns: 'common' })}
          onClick={props.onClose}
        />
      </InspectorHeader>
      <Sections>
        {object.attributes && (
          <IfcSection
            title={t('layerVisibilityDialog.ifc.attributes', { ns: 'skyviewAssetMenu' })}
            values={object.attributes}
          />
        )}
        {object.type && (
          <IfcSection
            title={`${t('layerVisibilityDialog.ifc.type', { ns: 'skyviewAssetMenu' })}: ${object.type.class ?? ''}`}
            values={object.type.attributes ?? {}}
          />
        )}
        {Object.entries(object.propertySets ?? {}).map(([name, values]) => (
          <IfcSection key={`p:${name}`} title={name} values={values} />
        ))}
        {Object.entries(object.quantitySets ?? {}).map(([name, values]) => (
          <IfcSection key={`q:${name}`} title={name} values={values} />
        ))}
        {object.classifications?.map((classification, i) => (
          <IfcSection
            key={`c:${i}`}
            title={t('layerVisibilityDialog.ifc.classification', { ns: 'skyviewAssetMenu' })}
            values={classification}
          />
        ))}
        {object.materials && object.materials.length > 0 && (
          <IfcSection
            title={t('layerVisibilityDialog.ifc.materials', { ns: 'skyviewAssetMenu' })}
            values={Object.fromEntries(object.materials.map((x, i) => [i + 1, x]))}
          />
        )}
      </Sections>
    </Inspector>
  );
};

/** The tree scrolls by itself while an object is inspected, which is shown below it. */
/**
 * The tree, and the inspector below it while an object is inspected, which share the height of the
 * dialog: the tree takes up to half of it, and the inspector the rest, also in a large window.
 */
const Panes = styled.div<{ $inspecting: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-rows: ${(props) =>
    props.$inspecting ? 'fit-content(50%) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
`;

const Tree = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 1em;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.div<{ $level: number; $dimmed: boolean; $selectable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25em;
  padding-left: ${(props) => props.$level * 1.25}em;
  line-height: 1.8em;
  opacity: ${(props) => (props.$dimmed ? 0.5 : 1)};
  cursor: ${(props) => (props.$selectable ? 'pointer' : 'default')};

  ${(props) =>
    props.$selectable &&
    `
      &:hover,
      &[data-hovered='true'] {
        background-color: #fff1ae;
      }
    `}

  &[data-inspected='true'] {
    background-color: ${({ theme }) => theme.color.selected};
  }
`;

const Expander = styled.div<{ $hidden: boolean }>`
  cursor: pointer;
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
`;

const Name = styled.span`
  overflow-wrap: anywhere;
`;

const Inspector = styled.section`
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ccc;
`;

const InspectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5em;
  padding: 0.5em 1em;
  background-color: #f6f6f6;
`;

const Sections = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1em 0.5em 1em;
`;

const Section = styled.details`
  margin-top: 0.5em;

  summary {
    cursor: pointer;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    padding: 0.1em 0.5em 0.1em 0;
    vertical-align: top;
    overflow-wrap: anywhere;
  }
`;

const Key = styled.td<{ $level: number }>`
  padding-left: ${(props) => 1 + props.$level}em !important;
  color: #666;
  width: 45%;
`;

const Value = styled.td`
  user-select: text;
`;
